import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CatalogPagination, CatalogProduct } from '../../types'
import { fetchCategoryProducts, type CatalogProductListingFilters } from '../../hooks/runtime/catalogApi'
import { clampPage, syncProductsPageQuery } from './paginationQuery'

type Params = {
  categoryId?: string
  primaryCategoryId?: string
  initialProducts: CatalogProduct[]
  initialPagination?: CatalogPagination
  pageSize: number
  page: number
  setPage: (page: number) => void
  filters?: CatalogProductListingFilters
}

const buildFallbackPagination = (products: CatalogProduct[], pageSize: number): CatalogPagination => {
  const total = products.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  return {
    page: 1,
    limit: pageSize,
    total,
    totalPages,
    hasNextPage: false,
    hasPreviousPage: false,
  }
}

export const useCategoryProductsPagination = ({
  categoryId,
  primaryCategoryId,
  initialProducts,
  initialPagination,
  pageSize,
  page,
  setPage,
  filters,
}: Params) => {
  const [pagedProducts, setPagedProducts] = useState(initialProducts)
  const [productsLoading, setProductsLoading] = useState(Boolean(categoryId) && !initialPagination)
  const [pagination, setPagination] = useState<CatalogPagination>(
    initialPagination ?? buildFallbackPagination(initialProducts, pageSize),
  )
  const [facets, setFacets] = useState<NonNullable<Awaited<ReturnType<typeof fetchCategoryProducts>>['facets']>>()
  const totalProductPages = pagination.totalPages
  const productsPage = clampPage(page, totalProductPages)
  const filterKey = JSON.stringify(filters ?? {})

  useEffect(() => {
    if (!categoryId) {
      if (page !== 1) setPage(1)
      syncProductsPageQuery(1, 'replace')
      return
    }
    if (page !== productsPage) setPage(productsPage)
    syncProductsPageQuery(productsPage, 'replace')
  }, [categoryId, page, productsPage, setPage, totalProductPages])

  useEffect(() => {
    if (!categoryId) {
      setPagedProducts(initialProducts)
      setPagination(initialPagination ?? buildFallbackPagination(initialProducts, pageSize))
      setFacets(undefined)
      setProductsLoading(false)
      return
    }

    const controller = new AbortController()
    const load = async () => {
      setProductsLoading(true)
      try {
        const response = await fetchCategoryProducts(categoryId, controller.signal, {
          page: productsPage,
          limit: pageSize,
          filters,
          primaryCategoryId,
        })
        if (controller.signal.aborted) return
        setPagedProducts(response.products)
        setPagination(response.pagination)
        setFacets(response.facets)
        setProductsLoading(false)
      } catch {
        if (controller.signal.aborted) return
        setProductsLoading(false)
      }
    }
    void load()
    return () => controller.abort()
  }, [categoryId, filterKey, filters, initialPagination, initialProducts, pageSize, primaryCategoryId, productsPage])

  const changePage = useCallback(
    (page: number) => {
      const nextPage = clampPage(page, totalProductPages)
      if (nextPage === productsPage) return
      setPage(nextPage)
      syncProductsPageQuery(nextPage, 'push')
    },
    [productsPage, setPage, totalProductPages],
  )

  return useMemo(
    () => ({
      productsPage,
      totalProductPages,
      pagedProducts,
      totalItems: pagination.total,
      productsLoading,
      facets,
      changePage,
    }),
    [changePage, facets, pagedProducts, pagination.total, productsLoading, productsPage, totalProductPages],
  )
}
